import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Clock, Users, Globe, Loader2, AlertCircle, Download, MessageCircle, Share2, Sparkles } from 'lucide-react';
import { publicApiClient } from '../api/client';
import { Button, Card, Modal } from '../components/UIComponents';
import EditableSegment from '../components/EditableSegment';
import ExportTranscript, { type ExportOptions } from '../components/ExportTranscript';
import CopyLinkDialog from '../components/CopyLinkDialog';
import { MindMapViewer } from '../components/MindMapViewer';
import type { TranscriptionSegment } from '../api/transcriptions';
import { buildExportFilename, downloadBlob, generateJSON, generateMarkdown, generateSRT, generateTXT, generateVTT } from '../utils/exportUtils';
import { stripKnownTranscriptionTitleExtension } from '../utils/transcriptionTitle';
import { useScrollBoundary, getScrollBehavior } from '../hooks/useScrollBoundary';
import { TranscriptScrollFab } from '../components/TranscriptScrollFab';
import { useTranslation } from 'react-i18next';

interface TranslationData {
  segments: TranscriptionSegment[];
}

interface PublicMindmap {
  source_version: string;
  outline_markdown: string;
  created_at: string;
}

interface PublicShareResponse {
  display_name: string;
  display_name_user_set?: boolean;
  duration: number;
  language: string;
  word_count: number;
  speaker_count: number;
  content: string;
  segments: TranscriptionSegment[];
  speakers?: { id: string; name: string }[];
  translations?: Record<string, TranslationData>;
  mindmap?: PublicMindmap;
  created_at: string;
}

type LoadingState = 'loading' | 'success' | 'not_found' | 'expired' | 'error';

type ShareTab = 'transcript' | 'mindmap' | 'qa';

const PublicSharePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<LoadingState>('loading');
  const [data, setData] = useState<PublicShareResponse | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('original');
  const [error, setError] = useState<string>('');
  const [activeTab, setActiveTab] = useState<ShareTab>('transcript');
  const [activeSegmentId, setActiveSegmentId] = useState<number | null>(null);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const segmentsListRef = useRef<HTMLDivElement>(null);

  const { isScrollable, isAtBottom, update: updateScrollBoundary } = useScrollBoundary(segmentsListRef);

  const handleScrollToTop = useCallback(() => {
    segmentsListRef.current?.scrollTo({ top: 0, behavior: getScrollBehavior() });
  }, []);

  const handleScrollToBottom = useCallback(() => {
    const el = segmentsListRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: getScrollBehavior() });
  }, []);

  const displayTitle = useMemo(() => {
    const fallback = t('publicShare.fallbackTitle');
    if (!data?.display_name) return fallback;
    if (data.display_name_user_set) return data.display_name.trim() || fallback;
    return stripKnownTranscriptionTitleExtension(data.display_name) || fallback;
  }, [data?.display_name, data?.display_name_user_set, t]);

  const loadShare = useCallback(async () => {
    if (!token) return;

    setState('loading');
    try {
      const response = await publicApiClient.get<PublicShareResponse>(
        `/public/share/${token}`
      );
      setData(response.data);
      setState('success');
    } catch (err: unknown) {
      const axiosError = err as { response?: { status?: number; data?: { error?: string } } };
      if (axiosError.response?.status === 410) {
        setState('expired');
      } else if (axiosError.response?.status === 404) {
        setState('not_found');
       } else {
         setError(axiosError.response?.data?.error || t('publicShare.errors.failedToLoad'));
         setState('error');
       }
     }
   }, [token, t]);

  useEffect(() => {
    if (token) {
      loadShare();
    }
  }, [token, loadShare]);

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatTimestamp = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getAvailableLanguages = () => {
    if (!data) return [];
    const languages = [{ code: 'original', label: data.language || t('publicShare.languageOriginal') }];
    if (data.translations) {
      Object.keys(data.translations).forEach((lang) => {
        languages.push({ code: lang, label: lang });
      });
    }
    return languages;
  };

  const getCurrentSegments = (): TranscriptionSegment[] => {
    if (!data) return [];
    if (selectedLanguage === 'original') {
      return data.segments;
    }
    return data.translations?.[selectedLanguage]?.segments || data.segments;
  };

   const shareUrl = useMemo(() => {
     if (typeof window === 'undefined') return '';
     return window.location.href;
   }, []);

  const availableLanguages = getAvailableLanguages();
  const segments = getCurrentSegments();

  useEffect(() => {
    updateScrollBoundary();
  }, [segments.length, updateScrollBoundary]);

  const speakersById = useMemo(() => {
    return new Map((data?.speakers || []).map(s => [s.id, s.name]));
  }, [data?.speakers]);

  const getSpeakerDisplayName = useCallback((segment: TranscriptionSegment) => {
    const raw = segment.speaker?.trim() || '';
    if (!raw) return t('transcription.speakers.speaker');
    const mapped = speakersById.get(raw)?.trim();
    return mapped || raw;
  }, [speakersById, t]);

  const handleExportSubmit = (format: string, options: ExportOptions) => {
    let content = '';
    let mimeType = 'text/plain';
    let extension = format;

    try {
      switch (format) {
        case 'txt':
          content = generateTXT(segments, options);
          mimeType = 'text/plain';
          break;
        case 'json':
          content = generateJSON(segments);
          mimeType = 'application/json';
          break;
        case 'md':
          content = generateMarkdown(segments, options);
          mimeType = 'text/markdown';
          break;
        case 'srt':
          content = generateSRT(segments);
          mimeType = 'text/plain';
          break;
        case 'vtt':
          content = generateVTT(segments);
          mimeType = 'text/vtt';
          break;
        case 'docx':
        case 'pdf':
          setIsExportOpen(false);
          return;
        default:
          setIsExportOpen(false);
          return;
      }

      const filename = buildExportFilename(displayTitle, extension, data?.display_name_user_set !== true);
      downloadBlob(content, filename, mimeType);
      setIsExportOpen(false);
    } catch {
      setIsExportOpen(false);
    }
  };

  // Render different states
  if (state === 'loading') {
    return (
      <div className="min-h-[60vh] bg-slate-50 dark:bg-zinc-950 flex items-center justify-center rounded-xl border border-slate-200 dark:border-zinc-800">
        <Loader2 className="h-12 w-12 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (state === 'not_found') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <AlertCircle className="h-16 w-16 text-slate-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 dark:text-zinc-100 mb-2">
             {t('publicShare.states.notFoundTitle')}
          </h1>
          <p className="text-slate-600 dark:text-zinc-400 mb-6">
            {t('publicShare.states.notFoundDescription')}
          </p>
          <Button onClick={() => navigate('/')}>{t('publicShare.actions.goToOmniScribe')}</Button>
        </div>
      </div>
    );
  }

  if (state === 'expired') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <Clock className="h-16 w-16 text-amber-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 dark:text-zinc-100 mb-2">
            {t('publicShare.states.expiredTitle')}
          </h1>
          <p className="text-slate-600 dark:text-zinc-400 mb-6">
            {t('publicShare.states.expiredDescription')}
          </p>
          <Button onClick={() => navigate('/')}>{t('publicShare.actions.goToOmniScribe')}</Button>
        </div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 dark:text-zinc-100 mb-2">
            {t('publicShare.states.errorTitle')}
          </h1>
          <p className="text-slate-600 dark:text-zinc-400 mb-6">{error}</p>
          <Button onClick={loadShare}>{t('publicShare.actions.tryAgain')}</Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{t('publicShare.pageTitle', { title: displayTitle })}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="bg-slate-50 dark:bg-zinc-950 min-h-[calc(100vh-64px)]">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Left: Transcription Detail */}
            <div className="flex-1 min-w-0">
              <Card className="overflow-hidden">
                {/* Toolbar */}
                <div className="flex items-center justify-between gap-4 px-4 py-3 border-b border-slate-100 dark:border-zinc-800">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setActiveTab('transcript')}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        activeTab === 'transcript'
                          ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700'
                      }`}
                    >
                      {t('transcription.tabs.transcript')}
                    </button>
                    <button
                      onClick={() => setActiveTab('mindmap')}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        activeTab === 'mindmap'
                          ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700'
                      }`}
                    >
                      {t('transcription.tabs.mindmap')}
                    </button>
                    <button
                      onClick={() => setActiveTab('qa')}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        activeTab === 'qa'
                          ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700'
                      }`}
                    >
                      {t('transcription.tabs.keypoints')}
                    </button>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setIsShareOpen(true)} title={t('publicShare.actions.share')}>
                      <Share2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate('/register')}
                      title={t('publicShare.actions.askQuestionsSignup')}
                    >
                      <MessageCircle className="h-4 w-4" />
                    </Button>
                    <Button variant="primary" size="sm" onClick={() => setIsExportOpen(true)}>
                      <Download className="h-4 w-4 me-2" />
                      {t('publicShare.actions.exportTranscript')}
                    </Button>
                  </div>
                </div>

                {/* Title + meta */}
                <div className="px-4 py-4 border-b border-slate-100 dark:border-zinc-800 bg-white dark:bg-zinc-900">
                  <h1 className="text-lg sm:text-xl font-semibold text-slate-900 dark:text-zinc-100">
                    {displayTitle}
                  </h1>
                  <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-600 dark:text-zinc-400">
                    <div className="flex items-center gap-1.5">
                      <Clock className="h-4 w-4" />
                      <span>{formatDuration(data?.duration || 0)}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Users className="h-4 w-4" />
                        <span>
                          {t('publicShare.meta.speakersCount', { count: data?.speaker_count ?? 0 })}
                        </span>
                    </div>
                    {data?.language && (
                      <div className="flex items-center gap-1.5">
                        <Globe className="h-4 w-4" />
                        <span>{data.language}</span>
                      </div>
                    )}
                  </div>

                  {availableLanguages.length > 1 && (
                    <div className="mt-4 pt-4 border-t border-slate-100 dark:border-zinc-800">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-slate-700 dark:text-zinc-300 me-1">
                          {t('publicShare.actions.languageLabel')}
                        </span>
                        {availableLanguages.map((lang) => (
                          <button
                            key={lang.code}
                            onClick={() => setSelectedLanguage(lang.code)}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                              selectedLanguage === lang.code
                                ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700'
                            }`}
                          >
                            {lang.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Body */}
                <div className="bg-white dark:bg-zinc-900">
                  {activeTab === 'transcript' && (
                    <div className="flex flex-col">
                      <div className="px-4 pt-4">
                        <div className="flex items-center justify-between">
                          <h2 className="text-sm font-semibold text-slate-900 dark:text-zinc-100">
                            {t('publicShare.aiPreview.title')}
                          </h2>
                          <Button variant="ghost" size="sm" onClick={() => navigate('/register')}>
                            <Sparkles className="h-4 w-4 me-2" />
                            {t('publicShare.actions.generate')}
                          </Button>
                        </div>
                          <p className="mt-2 text-sm text-slate-600 dark:text-zinc-400">
                            {t('publicShare.aiPreview.description')}
                          </p>
                      </div>

                      <div className="mt-4 border-t border-slate-100 dark:border-zinc-800" />

                      <div className="relative max-h-[60vh]">
                        <div ref={segmentsListRef} className="max-h-[60vh] overflow-y-auto custom-scrollbar">
                          {segments.length === 0 ? (
                            <div className="flex items-center justify-center h-64 text-slate-400 dark:text-zinc-500">
                              {t('publicShare.emptyTranscript')}
                            </div>
                          ) : (
                            segments.map((segment) => (
                              <EditableSegment
                                key={segment.id}
                                segment={segment}
                                speakerNameOverride={getSpeakerDisplayName(segment)}
                                isActive={activeSegmentId === segment.id}
                                isEditing={false}
                                editingText=""
                                onTextChange={() => {}}
                                onEdit={() => {}}
                                onSave={() => {}}
                                onCancel={() => {}}
                                onClick={() => setActiveSegmentId(segment.id)}
                                onPlay={() => {}}
                                formatTime={formatTimestamp}
                                readOnly
                                showPlayButton={false}
                                showEditButton={false}
                              />
                            ))
                          )}
                        </div>
                        <TranscriptScrollFab
                          isScrollable={isScrollable}
                          isAtBottom={isAtBottom}
                          onScrollToTop={handleScrollToTop}
                          onScrollToBottom={handleScrollToBottom}
                        />
                      </div>
                    </div>
                  )}

                  {activeTab !== 'transcript' && (
                    <>
                      {activeTab === 'mindmap' && data?.mindmap?.outline_markdown ? (
                        <div className="relative h-[70vh] overflow-hidden">
                          <MindMapViewer markdown={data.mindmap.outline_markdown} />
                        </div>
                      ) : (
                        <div className="p-8 text-center">
                          <div className="mx-auto h-14 w-14 rounded-2xl bg-slate-100 dark:bg-zinc-800 flex items-center justify-center mb-4">
                            <Sparkles className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
                          </div>
                          <h2 className="text-lg font-semibold text-slate-900 dark:text-zinc-100 mb-2">
                            {t('publicShare.upsell.featureAvailable', {
                              feature: activeTab === 'mindmap' ? t('transcription.tabs.mindmap') : t('publicShare.qaLabel')
                            })}
                          </h2>
                          <p className="text-sm text-slate-600 dark:text-zinc-400 mb-6">
                            {activeTab === 'mindmap'
                              ? t('publicShare.upsell.mindmapHelp')
                              : t('publicShare.upsell.qaHelp')}
                          </p>
                          <Button onClick={() => navigate('/register')}>{t('publicShare.actions.startForFree')}</Button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </Card>
            </div>

             {/* Right: Promotion Panel */}
             <div className="w-full lg:w-[360px]">
               <div className="lg:sticky lg:top-24">
                 <Card className="p-6">
                   <h2 className="text-xl font-bold text-slate-900 dark:text-zinc-100">
                    {t('publicShare.upsell.title')}
                  </h2>
                  <ul className="mt-4 space-y-2 text-sm text-slate-600 dark:text-zinc-400">
                    {(() => {
                      const rawBullets = t('publicShare.upsell.bullets', { returnObjects: true }) as unknown;
                      const bullets = Array.isArray(rawBullets)
                        ? rawBullets.filter((item): item is string => typeof item === 'string')
                        : [];

                      return bullets.map((bullet) => <li key={bullet}>{bullet}</li>);
                    })()}
                  </ul>
                  <Button className="mt-6 w-full" onClick={() => navigate('/register')}>
                    {t('publicShare.actions.startForFree')}
                  </Button>
                 </Card>
               </div>
             </div>
          </div>
        </div>

        <CopyLinkDialog
          isOpen={isShareOpen}
          onClose={() => setIsShareOpen(false)}
          url={shareUrl}
          title={t('publicShare.copyLink.title')}
          description={t('publicShare.copyLink.description')}
        />

        <Modal
          isOpen={isExportOpen}
          onClose={() => setIsExportOpen(false)}
          title={t('transcription.export.title')}
        >
          <ExportTranscript
            onExport={handleExportSubmit}
            onCancel={() => setIsExportOpen(false)}
          />
        </Modal>
      </div>
    </>
  );
};

export default PublicSharePage;
